/* istanbul ignore file - tested e2e */

/**
 * NB:
 * This page will likely to be made redundant in the near future when
 * instructions is properly incorporated into the product management process.
 *
 * Hook usage in this page for file upload is largely a copy of that found in
 * the equivalent create product page and as such it has not been refactored or
 * cleaned up to avoid duplication of effort.
 */

import { acceptedFileFormats, cadExportInstructions } from '@constants/URLs';
import {
  faCloudExclamation,
  faExternalLink,
  faFileCad,
  faRight,
  faWandMagicSparkles,
} from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCADUpload } from '@hooks/useCADUpload';
import { ProductDetails } from '@partful/entities/product/details';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import {
  WizardPageContent,
  WizardPageFooter,
  WizardPageHeader,
  WizardPageLayout,
} from '@partful/shared/ui/layouts/WizardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { getCADJob, patchCADJob, postCAD } from '@services/productInventory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatFileUpload } from '@utils/fileUploadFormat';
import { useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation } from 'react-router';
import { useGoogleAnalytics } from 'src/v2/shared/lib/hooks/useGoogleAnalytics';
import styles from './DetailsPage.module.scss';
import { UploadingPage } from './ui/UploadingPage';

export function DetailsPage() {
  const { t: i18n } = useTranslation();
  const { accountId } = useTenant();
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location?.search), [location?.search]);
  const initialRunID = searchParams.get('runID');

  const { sendEvent } = useGoogleAnalytics();

  const form = useForm<ProductDetails>({
    resolver: zodResolver(ProductDetails),
    mode: 'onChange',
    defaultValues: { productName: '' },
  });

  const values = form.watch();

  const setRunIDSearchParam = useCallback(
    (newRunID: string) => {
      const currentRunID = searchParams.get('runID');
      if (currentRunID !== newRunID) {
        searchParams.set('runID', newRunID);
        history.replace({ ...location, search: searchParams.toString() });
      }
    },
    [searchParams, history, location]
  );

  const deleteRunIDSearchParam = useCallback(() => {
    if (searchParams.has('runID')) {
      searchParams.delete('runID');
      history.replace({ ...location, search: searchParams.toString() });
    }
  }, [searchParams, history, location]);

  const {
    isLoading: isPOSTJobLoading,
    isError: isPOSTJobError,
    mutateAsync: postCADAsyncMutation,
    reset: resetPostJobError,
  } = useMutation(
    ['postCADMutation', initialRunID],
    async () => {
      const res = await postCAD(values.productName, accountId);
      if (initialRunID !== null) {
        await patchCADJob(initialRunID, accountId, { status: 'FAILED' });
        deleteRunIDSearchParam();
      }
      return res;
    },
    {
      onSuccess: async (response) => {
        flushSync(() => {
          setUploadedCadFile([
            formatFileUpload(values.cadFile.item(0)!, values.cadFile.item(0)?.name ?? '', response?.uploadURL!),
          ]);
          setRunID(response?.runID!);
        });
      },
    }
  );

  const {
    uploadFile,
    handleCancelUpload,
    setUploadedCadFile,
    newUploadStatuses,
    isLoading,
    isCADJobPatchLoading,
    isError,
    isUploadComplete,
    setRunID,
    runID,
    reset,
  } = useCADUpload(accountId, setRunIDSearchParam);

  const canFetchCadInfo = Boolean(runID) && isUploadComplete;
  const {
    isFetching: isLoadingCadInfo,
    isFetched: fetchedCadInfo,
    isError: isCadError,
    data: cadRunInfo,
  } = useQuery(['getCadRunInfo', runID], () => getCADJob(runID, accountId), {
    enabled: canFetchCadInfo,
  });

  const resetFailedUpload = () => {
    reset();
    resetPostJobError();
    setRunID('');
  };

  const isCadUploadFailed = isError || isPOSTJobError;
  const isCadInvalid = fetchedCadInfo && (isCadError || !cadRunInfo || cadRunInfo.fileDetails.length <= 0);

  const handleSubmit = async (_data: ProductDetails) => {
    try {
      await postCADAsyncMutation();
      await uploadFile();
    } catch (e) {
      // don't need to explicitly handle this as the useMutation will handle it instead
      // but still need to catch the async mutate function in case of error otherwise there is an uncaught promise rejection
    }
  };

  if (fetchedCadInfo && !isCadUploadFailed && !isCadInvalid) {
    return (
      <Redirect
        to={{
          pathname: `/instructions/create/general-assembly`,
          search: `?runID=${runID}&productName=${values.productName}`,
        }}
      />
    );
  }

  const errorIcon = isCadInvalid ? faFileCad : faCloudExclamation;
  const errorTitleKey = isCadInvalid
    ? 'instructions:create.CAD.fileValidationFailed.title'
    : 'instructions:create.CAD.uploadFailed.title';
  const errorMessageKey = isCadInvalid
    ? 'instructions:create.CAD.fileValidationFailed.message'
    : 'instructions:create.CAD.uploadFailed.message';

  if (isCadUploadFailed || isCadInvalid) {
    return (
      <WizardErrorPage
        pageTitle={
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
          </>
        }
        pageSubtitle={i18n('instructions:create.CAD.uploadTitle')}
        errorIcon={<FontAwesomeIcon icon={errorIcon} />}
        errorTitle={i18n(errorTitleKey, { filename: values.cadFile?.item(0)?.name })}
        errorMessage={
          <Typography as="p">
            <Trans i18nKey={errorMessageKey}>
              <Typography as="a" href={cadExportInstructions} target="_blank" rel="noreferrer" />
            </Trans>
          </Typography>
        }
        errorActions={<Button onClick={resetFailedUpload}>{i18n('instructions:create.CAD.reuploadFile')}</Button>}
      />
    );
  }

  if (isLoading || isLoadingCadInfo) {
    return (
      <UploadingPage
        filename={newUploadStatuses[0]?.[0] ?? ''}
        remainingTime={newUploadStatuses[0]?.[1].remainingTime}
        progressPercentage={newUploadStatuses[0]?.[1].percentage}
        onCancel={handleCancelUpload}
      />
    );
  }

  if (isCADJobPatchLoading) {
    return <LoadingPage />;
  }

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <Typography appearance="heading5" as="h1" data-testid="create-title">
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
        </Typography>
        <Typography appearance="heading6" as="h2" data-testid="details-title">
          {i18n('instructions:create.CAD.detailsTitle')}
        </Typography>
        <Typography as="p">{i18n('instructions:create.CAD.detailsDescription')}</Typography>
      </WizardPageHeader>
      <WizardPageContent className={styles.formContainer}>
        <form
          id="detailsForm"
          name="detailsForm"
          onSubmit={form.handleSubmit((data) => handleSubmit(data))}
          className={styles.form}
        >
          <div className={styles.formControl}>
            <Typography as="label" htmlFor="productName">
              {i18n('instructions:create.CAD.name.label')} <sup>({i18n('instructions:common.required')})</sup>
            </Typography>
            <Controller
              name="productName"
              control={form.control}
              render={({ field: { name, onBlur, onChange, value, disabled }, fieldState: { error } }) => (
                <>
                  <Input
                    id={name}
                    placeholder={i18n('instructions:create.CAD.name.placeholder')}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    disabled={disabled}
                  />
                  {error && (
                    <Typography appearance="label" className={styles.errorMessage}>
                      <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                      {i18n(error.message ?? 'instructions:create.name.validation.generic')}
                    </Typography>
                  )}
                </>
              )}
            />
          </div>
          <div className={styles.formControl}>
            <Typography as="label" htmlFor="cadFile">
              {i18n('instructions:create.CAD.file.label')} <sup>({i18n('instructions:common.zipFileRequired')})</sup>
            </Typography>
            <Controller
              name="cadFile"
              control={form.control}
              render={({ field: { name, onBlur, onChange, disabled }, fieldState: { error } }) => (
                <>
                  <Input
                    id={name}
                    name={name}
                    type="file"
                    accept=".zip"
                    placeholder={i18n('instructions:create.CAD.file.placeholder')}
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.files)}
                    onClick={() => sendEvent('instructions_cad_upload_choose_file_click')}
                    disabled={disabled}
                  />
                  {error && (
                    <Typography appearance="label" className={styles.errorMessage}>
                      <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                      {i18n(error.message ?? 'instructions:create.CAD.file.validation.generic')}
                    </Typography>
                  )}
                </>
              )}
            />
            <Button
              name="instructions_cad_upload_learn_cad_formats_click"
              variant="secondary"
              size="small"
              href={acceptedFileFormats}
              target="_blank"
              rel="noreferrer"
            >
              <span>
                {i18n('instructions:common.learnAcceptedCadFormats')} <FontAwesomeIcon icon={faExternalLink} />
              </span>
            </Button>
          </div>
        </form>
      </WizardPageContent>
      <WizardPageFooter>
        <Typography>{i18n('instructions:common.allChangesSaved')}</Typography>
        <Button
          type="submit"
          form="detailsForm"
          name="instructions_cad_upload_start_upload_click"
          size="large"
          disabled={!form.formState.isValid || isPOSTJobLoading}
          data-testid="upload-btn"
        >
          {i18n('instructions:create.CAD.startUpload')}
          <FontAwesomeIcon icon={faRight} />
        </Button>
      </WizardPageFooter>
    </WizardPageLayout>
  );
}
